import { cloneDeep, isEqual } from "lodash";
import { ActionTree, MutationTree } from "vuex";
import ProductSearchApi from "@/api/ProductSearchApi";
import {
  IPartnerProductSearchResult,
  IProductSearchResult,
  IActiveProductSearchQuery,
  IProductSearchQuery,
  IProductSearchResultFacet,
  IProductCategory,
  IPartnerProductSearchResultValue,
  IOrderRow,
  IProductData,
  IProduct,
  IRootState,
} from "@iloq/shared";
import config from "@/config";
import { handleCommonApiErrors } from "@/api/CommonApiErrorHandler";
import { productToOrderRow } from "@/util/ProductUtil";

const pageSize: number = parseInt(config.get("productSearchPageSize"), 10) || 30;

const productSearchApi = new ProductSearchApi();

export interface IProductSearchState {
  searchQuery: IActiveProductSearchQuery;
  searchResult: IPartnerProductSearchResult;
  categoryTree: IProductCategory;
  filteredCategoryTree: IProductCategory;
  selectedCategory: IProductCategory;
  searching: boolean;
  loadingMore: boolean;
}

interface IProductCategory2Way extends IProductCategory {
  children?: IProductCategory2Way[];
  parent?: IProductCategory2Way;
}

export interface IUiProductCategory extends IProductCategory2Way {
  matching?: boolean;
}

const defaultSort = "search.score() desc, sku_ asc";

const initialState: IProductSearchState = {
  searchQuery: {
    term: "",
    categories: [],
    top: pageSize,
    skip: 0,
    sort: defaultSort,
  },
  searchResult: {
    count: 0,
    facets: {
      categories: [],
    },
    value: [],
  },
  categoryTree: {
    id: "",
    label: "",
  },
  filteredCategoryTree: {
    id: "",
    label: "",
  },
  selectedCategory: {
    id: "",
    label: "",
  },
  searching: false,
  loadingMore: false,
};

const removeEmptyCategories = (
  cat: IProductCategory,
  idsOfNonemptyCategories: string[],
): IUiProductCategory => {
  const filteredChildren: IProductCategory[] = [];
  (cat.children || []).forEach((child) => {
    const filteredChild = removeEmptyCategories(child, idsOfNonemptyCategories);
    if (filteredChild.children!.length > 0 || idsOfNonemptyCategories.includes(filteredChild.id)) {
      filteredChild.matching = true;
      filteredChildren.push(filteredChild);
    }
  });
  cat.children = filteredChildren;
  return cat;
};

const make2WayTree = (cat: IProductCategory): IProductCategory2Way => {
  const cat2Way = { ...cat };
  cat2Way.children = (cat2Way.children || []).map((child) => {
    const child2Way = make2WayTree(child);
    child2Way.parent = cat2Way;
    return child2Way;
  });
  return cat2Way;
};

const sortCategoryTree = (subItem: IUiProductCategory | null | undefined): void => {
  if (subItem && subItem.children) {
    subItem.children.sort(
      (c1: IUiProductCategory, c2: IUiProductCategory): number =>
        (c1.displayOrder || -1) - (c2.displayOrder || -1),
    );
    subItem.children.forEach(sortCategoryTree);
  }
};

const findCategory = (tree: IUiProductCategory, id: string): IUiProductCategory | undefined => {
  if (tree.id === id) {
    return tree;
  }
  let result: IUiProductCategory | undefined;
  (tree.children || []).find((child) => {
    const childResult = findCategory(child, id);
    if (childResult) {
      result = childResult;
      return true;
    }
    return false;
  });
  return result;
};

const processMatchingsForCategoryTree = (
  searchResult: IPartnerProductSearchResult,
  searchState: IProductSearchState,
): IProductCategory => {
  const facetCategoryIds = searchResult.facets.categories.map(
    (cat: IProductSearchResultFacet) => cat.value,
  );
  const filteredCategoryTree = cloneDeep(searchState.categoryTree);

  const setMatchings = (subtree: IUiProductCategory): { matches: number } => {
    const isNodeMatching = facetCategoryIds.indexOf(subtree.id) >= 0;
    const subtreeStatus = {
      matches: isNodeMatching ? 1 : 0,
    };

    (subtree.children || []).forEach((child: IProductCategory) => {
      const childStatus = setMatchings(child);
      subtreeStatus.matches += childStatus.matches;
    });
    subtree.matching = subtreeStatus.matches > 0;
    return subtreeStatus;
  };
  setMatchings(filteredCategoryTree);

  sortCategoryTree(filteredCategoryTree);

  return filteredCategoryTree;
};

const actions: ActionTree<IProductSearchState, IRootState> = {
  initializeState({ commit, dispatch, state }, { salesRegion }): void {
    commit("setSalesRegion", salesRegion);
    productSearchApi
      .fetchCategoryTree()
      .then((categoryTree: IProductCategory) => {
        dispatch("searchProducts").then(() => {
          const idsOfNonemptyCategories = state.searchResult.facets.categories.map(
            (cat) => cat.value,
          );
          const res2way = make2WayTree(
            removeEmptyCategories(categoryTree, idsOfNonemptyCategories),
          );
          sortCategoryTree(res2way);
          commit("setCategoryTree", res2way);
          const filteredCategoryTree = cloneDeep(res2way);
          commit("setFilteredCategoryTree", filteredCategoryTree);
          commit("setSelectedCategory", filteredCategoryTree);
        });
      })
      .catch(handleCommonApiErrors);
  },
  clearSearch({ commit, dispatch, state }): void {
    commit("setTerm", "");
    commit("setCategories", []);
    dispatch("searchProducts");
    commit("setFilteredCategoryTree", cloneDeep(state.categoryTree));
  },
  updateTerm({ commit, dispatch }, term: string): void {
    commit("setTerm", term);
    dispatch("searchProducts");
  },
  updateTermWithoutSearching({ commit }, term: string): void {
    commit("setTerm", term);
  },
  updateCategories({ commit, dispatch }, categories: string[]): void {
    commit("setCategories", categories);
    dispatch("searchProducts");
  },
  updateSelectedCategory({ commit, dispatch, state }, selectedCategory: IUiProductCategory): void {
    commit("setSelectedCategory", findCategory(state.filteredCategoryTree, selectedCategory.id));
    const selectedIds: string[] = [];
    const gatherSelections = (itemToGather: IUiProductCategory): void => {
      selectedIds.push(itemToGather.id);
      (itemToGather.children || []).forEach(gatherSelections);
    };
    gatherSelections(selectedCategory);
    dispatch("updateCategories", selectedIds);
  },
  async searchProducts({ commit, state }): Promise<void> {
    commit("setSearching", true);
    commit("setLoadingMore", false);

    const query = cloneDeep(state.searchQuery);

    const search = productSearchApi
      .searchActivePartnerProducts(query)
      .then((res: IPartnerProductSearchResult) => {
        // Ignore results if query has changed during the search was running
        if (isEqual(query, state.searchQuery)) {
          commit("setSearchResult", res);
          commit("setSearching", false);
        }
      });

    const queryForUnfilteredFacets: IActiveProductSearchQuery = {
      term: query.term,
      categories: [],
      salesRegion: query.salesRegion,
      top: 1,
      skip: 0,
    };
    const queryUnfilteredFacets = productSearchApi
      .searchActivePartnerProducts(queryForUnfilteredFacets)
      .then((res: IPartnerProductSearchResult) => {
        // Ignore results if query has changed during the search was running
        if (isEqual(query, state.searchQuery)) {
          commit("setFilteredCategoryTree", processMatchingsForCategoryTree(res, state));
        }
      });

    await Promise.allSettled([search, queryUnfilteredFacets]).catch(handleCommonApiErrors);
  },
  loadMoreProducts({ commit, state }): void {
    const searchResult = state.searchResult;
    if (state.loadingMore || searchResult.value.length >= searchResult.count) {
      return;
    }
    commit("setLoadingMore", true);

    const originalQuery = cloneDeep(state.searchQuery);
    const query = cloneDeep(state.searchQuery);
    query.skip = state.searchResult.value.length;

    productSearchApi
      .searchActivePartnerProducts(query)
      .then((res: IPartnerProductSearchResult) => {
        // Ignore results if query has changed during the load was running
        if (isEqual(originalQuery, state.searchQuery)) {
          commit("addSearchResults", res.value);
          commit("setLoadingMore", false);
        }
      })
      .catch(handleCommonApiErrors);
  },
  searchProductsQuick(
    { state },
    searchQuery: IActiveProductSearchQuery,
  ): Promise<IPartnerProductSearchResult | void> {
    searchQuery.salesRegion = state.searchQuery.salesRegion;
    searchQuery.sort = searchQuery.sort || defaultSort;
    return productSearchApi.searchActivePartnerProducts(searchQuery).catch(handleCommonApiErrors);
  },
  searchArchivedProductsQuick(
    { state },
    searchQuery: IProductSearchQuery,
  ): Promise<IProductSearchResult | void> {
    searchQuery.sort = searchQuery.sort || defaultSort;
    return productSearchApi.searchProducts(searchQuery).catch(handleCommonApiErrors);
  },
  ensureProductsExistWithLatestDataAndPrice(
    { rootGetters, rootState },
    { rows, failWhenNoProducts }: { rows: IOrderRow[]; failWhenNoProducts: boolean },
  ): Promise<IOrderRow[]> {
    const clonedRows = cloneDeep(rows);
    const serviceproductIds: string[] = (rootState as any).productData.serviceproducts.ids;

    const fetchPromises: Promise<IProductData | null | void>[] = [];
    const productSkusToBeSkipped: string[] = [];
    clonedRows
      .filter((row: IOrderRow) => !serviceproductIds.includes(row.product.id))
      .forEach((row: IOrderRow) => {
        const fetchPromise: Promise<IProductData | null | void> = productSearchApi
          .fetchProduct(row.product.id)
          .then((productFound: IProductData) => {
            const product: IProduct = productToOrderRow(productFound).product;
            // Ensure latest price for existing product
            row.product = product;

            // Skip products that the user is not allowed to order
            const orderingAllowed = rootGetters["orderingPermission/isOrderingAllowed"](
              product.family,
            );
            if (!orderingAllowed) {
              productSkusToBeSkipped.push(row.product.id);
              return null;
            }

            return productFound;
          })
          .catch(handleCommonApiErrors)
          .catch(() => {
            productSkusToBeSkipped.push(row.product.id);
            // Return null to make Promise.all always resolve.
            return null;
          });
        fetchPromises.push(fetchPromise);
      });

    return Promise.all(fetchPromises)
      .then((allProductsFetched: any) => {
        const existingProducts = allProductsFetched.filter(
          (item: IProductData | null) => item !== null,
        );
        if (existingProducts.length === 0 && failWhenNoProducts) {
          return Promise.reject(new Error("All products were missing"));
        }

        const orderRowsThatExist: IOrderRow[] = clonedRows.filter(
          (row: IOrderRow) =>
            serviceproductIds.includes(row.product.id) ||
            !productSkusToBeSkipped.includes(row.product.id),
        );
        return Promise.resolve(orderRowsThatExist);
      })
      .catch((fetchFailed: any) => Promise.reject(fetchFailed));
  },
};

const mutations: MutationTree<IProductSearchState> = {
  setSearching(state: IProductSearchState, searching: boolean): void {
    state.searching = searching;
  },
  setLoadingMore(state: IProductSearchState, loadingMore: boolean): void {
    state.loadingMore = loadingMore;
  },
  setTerm(state: IProductSearchState, term: string): void {
    state.searchQuery.term = term.trim();
  },
  setCategories(state: IProductSearchState, categories: string[]): void {
    state.searchQuery.categories = categories;
  },
  setSalesRegion(state: IProductSearchState, salesRegion?: string): void {
    state.searchQuery.salesRegion = salesRegion;
  },
  setSearchResult(state: IProductSearchState, searchResult: IPartnerProductSearchResult): void {
    state.searchResult = searchResult;
  },
  addSearchResults(state: IProductSearchState, value: IPartnerProductSearchResultValue[]): void {
    state.searchResult.value.push(...value);
  },
  setCategoryTree(state: IProductSearchState, categoryTree: IProductCategory): void {
    state.categoryTree = categoryTree;
  },
  setFilteredCategoryTree(state: IProductSearchState, categoryTree: IProductCategory): void {
    state.filteredCategoryTree = categoryTree;
    state.selectedCategory = findCategory(state.filteredCategoryTree, state.selectedCategory.id)!;
  },
  setSelectedCategory(state: IProductSearchState, selectedCategory: IProductCategory): void {
    state.selectedCategory = selectedCategory;
  },
};

export const productSearchModule = {
  actions,
  mutations,
  state: initialState,
  namespaced: true,
};
